import { useCallback, useEffect, useState } from 'react'
import { debounce } from 'lodash'
import { OptionsOrGroups } from 'react-select'
import { GroupBase } from '@clickcease/cc-client-components-lib'
import { SelectOption } from 'Types/SelectOption'
import useGoogleMapLocationStore from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/store/getGoogleMapLocationStore'
import { useSearchAddress } from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/api/useSearchAddress'
import { useGetPlaceDetails } from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/api/useGetPlaceDetails'
import {
  AddressComponent,
  GetPlaceDetailsResponseType,
} from 'Features/accountSettings/components/Billing/EditBillingDetailsModal/types'

interface AddressSearchResult {
  street: string
  city: string
  state: string
  postalCode: string
}

interface UseGoogleAddressSearchProps {
  onAddressSelect?: (address: AddressSearchResult) => void
}

interface AddressOption {
  label: string
  value: string
  addressComponents: AddressComponent[]
}

/**
 * Custom hook: useGoogleAddressSearch
 *
 * This hook enables real-time address search and selection using Google Maps API data.
 * It integrates with an AsyncSelect component, fetching and handling address details.
 *
 * Process:
 * 1. **Typing in AsyncSelect** triggers `loadOptions`, which debounces input and updates `useGoogleMapLocationStore`.
 * 2. **Fetching Address Suggestions** via `useSearchAddress()`. If results exist, they populate the dropdown; otherwise, a fallback option is used.
 * 3. **Handling Delayed Search Results**: If results arrive later, a stored callback updates the dropdown.
 * 4. **Selecting an Address** triggers `getPlaceDetails`. If details are missing, it updates `setPlaceId`, which enables `useGetPlaceDetails()`.
 * 5. **Fetching Detailed Address Info**: `useGetPlaceDetails()` extracts street, city, state, and postal code, passing them to `onAddressSelect`.
 *
 * Returns:
 * - `loadOptions`: Fetches address suggestions.
 * - `getPlaceDetails`: Handles selection.
 * - `isLoadingSearch`: Indicates loading state.
 * - `setCountry`: Restricts search by country.
 * - `placeDetails`: Contains detailed address data.
 */

export const useGoogleAddressSearch = ({ onAddressSelect }: UseGoogleAddressSearchProps = {}) => {
  const [latestCallback, setLatestCallback] = useState<((options: any) => void) | null>(null)
  const { setSearchString, setCountry, setPlaceId } = useGoogleMapLocationStore()
  const { data: searchAddressResult, isFetching: isLoadingSearch } = useSearchAddress()
  const { data: placeDetails } = useGetPlaceDetails() as { data: GetPlaceDetailsResponseType }

  const hasValidSearchResults = (results: any[]): boolean => results && results.length > 0

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchText: string) => {
      setSearchString(searchText)
    }, 700),
    []
  )

  const loadOptions = (
    inputValue: string,
    callback: (options: OptionsOrGroups<AddressOption, GroupBase<AddressOption>>) => void
  ) => {
    debouncedSearch(inputValue)

    if (hasValidSearchResults(searchAddressResult)) {
      const options = searchAddressResult?.map((result) => ({
        label: result.label,
        value: result.value,
        addressComponents: result.addressComponents,
      }))
      callback(options)
    } else if (searchAddressResult === null && inputValue) {
      const fallbackOption = [
        {
          label: inputValue,
          value: inputValue,
          addressComponents: [],
        },
      ]
      callback(fallbackOption)
    } else {
      setLatestCallback(() => callback)
    }
  }

  const getPlaceDetails = (option: SelectOption & { addressComponents?: AddressComponent[] }) => {
    if (searchAddressResult === null) {
      onAddressSelect?.({
        street: option.label,
        city: '',
        state: '',
        postalCode: '',
      })
    } else {
      if (option) {
        setPlaceId(option.value)
      }
    }
  }

  useEffect(() => {
    if (hasValidSearchResults(searchAddressResult)) {
      const options = searchAddressResult?.map((result) => ({
        label: result.label,
        value: result.value,
        addressComponents: result.addressComponents,
      }))

      if (latestCallback) {
        latestCallback(options)
      }
    } else if (searchAddressResult === null && latestCallback) {
      const { searchString } = useGoogleMapLocationStore.getState()
      if (searchString) {
        const fallbackOption = [
          {
            label: searchString,
            value: searchString,
            addressComponents: [],
          },
        ]
        latestCallback(fallbackOption)
      }
    }
  }, [searchAddressResult, latestCallback])

  useEffect(() => {
    if (placeDetails) {
      const { state, city, street, postalCode } = placeDetails
      onAddressSelect?.({
        street: street || '',
        city: city || '',
        state: state || '',
        postalCode: postalCode || '',
      })
    }
    // eslint-disable-next-line
  }, [placeDetails])

  return {
    loadOptions,
    getPlaceDetails,
    isLoadingSearch,
    setCountry,
    placeDetails,
  }
}
